import { type TxtImgFrontType, type TxtImgOptions, type TxtImgType } from '@Types/Blocks/TxtImg';

import Decorations from '../../_components/Decorations';
import TxtImgBlockFull from './variants/BlockFull';
import TxtImgClassic from './variants/Classic';
import TxtImgClassicTitle from './variants/ClassicTitle';
import TxtImgFullWidth from './variants/FullWidth';
import TxtImgHorizontal from './variants/Horizontal';

type TxtImgProps = Omit<TxtImgType & TxtImgOptions, 'block_theme'>;

export const TxtImg = (props: TxtImgProps) => {
	const {
		variant = 'classic',
		style = {},
		context = 'txtImg',
		ctasStyle,
		placement,
		sectionDecoration,
	} = props;

	const TxtImgTypeModel: TxtImgFrontType = {
		...props,
		description: props.description,
		variant: variant,
		context: context,
		sectionDecoration: sectionDecoration,
		headingFontClassName: {
			title: 'minos-txtImg-heading-title-font-style tablet:minos-txtImg-heading-title-font-style',
			text: {
				p: 'minos-txtImg-heading-description-font-style text-base tablet:minos-txtImg-heading-description-font-style my-4',
				a: {
					style: 'a',
					btnHasIcon: ctasStyle?.a?.btnHasIcon ? ctasStyle?.a?.btnHasIcon : false,
					btnDecorationElement: 'underline',
					aClassname: 'minos-txtImg-btn-a text-base tablet:minos-txtImg-btn-a',
				},
				btnPrimary: {
					style: 'primary',
					btnHasIcon: ctasStyle?.primary?.btnHasIcon ? ctasStyle?.primary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.primary?.btnDecorationElement
						? ctasStyle?.primary?.btnDecorationElement
						: 'short',
					primaryClassname: 'minos-txtImg-btn-primary tablet:minos-txtImg-btn-primary my-2 mr-4',
					animation: ctasStyle?.primary?.animation ? ctasStyle?.primary?.animation : 'none',
				},
				btnSecondary: {
					style: 'secondary',
					btnHasIcon: ctasStyle?.secondary?.btnHasIcon ? ctasStyle?.secondary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.secondary?.btnDecorationElement
						? ctasStyle?.secondary?.btnDecorationElement
						: 'short',
					secondaryClassname: 'minos-txtImg-btn-secondary tablet:minos-txtImg-btn-secondary my-2 mr-4',
					animation: ctasStyle?.secondary?.animation ? ctasStyle?.secondary?.animation : 'none',
				},
				btnTertiary: {
					style: 'tertiary',
					btnHasIcon: ctasStyle?.tertiary?.btnHasIcon ? ctasStyle?.tertiary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.tertiary?.btnDecorationElement
						? ctasStyle?.tertiary?.btnDecorationElement
						: 'short',
					tertiaryClassname: 'minos-txtImg-btn-tertiary tablet:minos-txtImg-btn-tertiary',
					animation: ctasStyle?.tertiary?.animation ? ctasStyle?.tertiary?.animation : 'none',
				},
			},
		},
		contentFontClass: {
			title: `minos-txtImg-inside-title-font-style tablet:minos-txtImg-inside-title-font-style ${
				variant != 'classicTitle' && 'tablet:mb-6 my-5'
			}`,
			text: {
				p: 'minos-txtImg-content-p text-base tablet:minos-txtImg-content-p my-4',
				h3: 'minos-txtImg-content-h3 text-2xl tablet:minos-txtImg-content-h3 mb-7',
				h4: 'minos-txtImg-content-h4 text-xl tablet:minos-txtImg-content-h4 mb-7',
				h5: 'minos-txtImg-content-h5 text-xl tablet:minos-txtImg-content-h5 mb-7',
				h6: 'minos-txtImg-content-h6 text-xl tablet:minos-txtImg-content-h6 mb-7',
				li: 'minos-txtImg-content-p text-base tablet:minos-txtImg-content-p before:text-minos-txtImg-list-color',
				a: {
					style: 'a',
					btnHasIcon: ctasStyle?.a?.btnHasIcon ? ctasStyle?.a?.btnHasIcon : false,
					btnDecorationElement: 'underline',
					aClassname: 'minos-txtImg-btn-a text-base tablet:minos-txtImg-btn-a',
				},
				btnPrimary: {
					style: 'primary',
					btnHasIcon: ctasStyle?.primary?.btnHasIcon ? ctasStyle?.primary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.primary?.btnDecorationElement
						? ctasStyle?.primary?.btnDecorationElement
						: 'short',
					primaryClassname: 'minos-txtImg-btn-primary tablet:minos-txtImg-btn-primary my-2 mr-4',
					animation: ctasStyle?.primary?.animation ? ctasStyle?.primary?.animation : 'none',
				},
				btnSecondary: {
					style: 'secondary',
					btnHasIcon: ctasStyle?.secondary?.btnHasIcon ? ctasStyle?.secondary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.secondary?.btnDecorationElement
						? ctasStyle?.secondary?.btnDecorationElement
						: 'short',
					secondaryClassname: 'minos-txtImg-btn-secondary tablet:minos-txtImg-btn-secondary my-2 mr-4',
					animation: ctasStyle?.secondary?.animation ? ctasStyle?.secondary?.animation : 'none',
				},
				btnTertiary: {
					style: 'tertiary',
					btnHasIcon: ctasStyle?.tertiary?.btnHasIcon ? ctasStyle?.tertiary?.btnHasIcon : false,
					btnDecorationElement: ctasStyle?.tertiary?.btnDecorationElement
						? ctasStyle?.tertiary?.btnDecorationElement
						: 'short',
					tertiaryClassname: 'minos-txtImg-btn-tertiary tablet:minos-txtImg-btn-tertiary',
					animation: ctasStyle?.tertiary?.animation ? ctasStyle?.tertiary?.animation : 'none',
				},
			},
		},
	};

	const variations = {
		classic: <TxtImgClassic {...TxtImgTypeModel} />,
		classicTitle: <TxtImgClassicTitle {...TxtImgTypeModel} />,
		horizontal: <TxtImgHorizontal {...TxtImgTypeModel} />,
		blockFull: <TxtImgBlockFull {...TxtImgTypeModel} />,
		fullWidth: <TxtImgFullWidth {...TxtImgTypeModel} />,
	};

	return (
		<>
			<section className='relative' data-variant={`txtImg-${variant}`} style={style}>
				<Decorations
					isSectionDecorated={sectionDecoration?.isSectionDecorated}
					type={sectionDecoration?.type}
					pathClassName={`${sectionDecoration?.type === 'wave' ? 'fill-minos-txtImg-bg-color' : 'fill-minos-decoration-bubble-left'}`}
					pathClassNameRightCircle={`${sectionDecoration?.type === 'bubble' && 'fill-minos-decoration-bubble-right'}`}
					placement={placement}
				>
					{variations[variant]}
				</Decorations>
			</section>
		</>
	);
};

export default TxtImg;
